import React, { useMemo } from 'react';
import { AlertCircle, CheckCircle2, Clock, EyeOff, Layers, Paperclip } from 'lucide-react';
import { dayLabel, fileHref, money, monthLabel, projectDot, qty, Spinner } from './ui';

// Factures enregistrées : une ligne par facture, groupées par mois (total HT du mois, par devise).

const PennylaneIcon = ({ status }) => {
  if (status === 'uploaded') return <CheckCircle2 className="h-3.5 w-3.5 text-emerald-500" title="Envoyée à Pennylane" />;
  if (status === 'failed') return <AlertCircle className="h-3.5 w-3.5 text-red-500" title="Échec de l'envoi à Pennylane" />;
  if (status === 'pending') return <Clock className="h-3.5 w-3.5 text-amber-500" title="Envoi à Pennylane en cours" />;
  if (status === 'excluded_amazon') return <EyeOff className="h-3.5 w-3.5 text-gray-400" title="Amazon : non envoyée à Pennylane" />;
  return null;
};

const ProjectsCell = ({ projects }) => {
  if (!projects?.length) return <span className="text-xs italic text-gray-400">sans projet</span>;
  const shown = projects.slice(0, 2);
  return (
    <div className="flex min-w-0 flex-wrap items-center gap-1">
      {shown.map((p) => (
        <span key={p.project_id} className="inline-flex max-w-[11rem] items-center gap-1 rounded-full bg-gray-50 px-1.5 py-0.5 text-[11px] text-gray-700 ring-1 ring-inset ring-gray-200"
          title={`${p.project_name} ${Math.round(p.percentage)} %`}>
          <span className={`h-1.5 w-1.5 shrink-0 rounded-full ${projectDot(p.project_id)}`} />
          <span className="truncate">{p.project_name}</span>
          {projects.length > 1 && <span className="shrink-0 text-gray-400">{Math.round(p.percentage)} %</span>}
        </span>
      ))}
      {projects.length > 2 && <span className="text-[11px] text-gray-500">+{projects.length - 2}</span>}
    </div>
  );
};

const InvoiceList = ({ items, total, loading, onOpen, onMore, selectedId }) => {
  const groups = useMemo(() => {
    const byMonth = new Map();
    items.forEach((item) => {
      const key = (item.date || '').slice(0, 7) || 'none';
      if (!byMonth.has(key)) byMonth.set(key, { key, label: monthLabel(item.date), items: [], totals: {} });
      const group = byMonth.get(key);
      group.items.push(item);
      const currency = item.currency || '€';
      group.totals[currency] = (group.totals[currency] || 0) + (Number(item.amount) || 0);
    });
    return [...byMonth.values()];
  }, [items]);

  if (!loading && !items.length) {
    return <p className="rounded-xl border border-gray-200 bg-white px-4 py-8 text-center text-sm text-gray-500">Aucune facture pour ces critères.</p>;
  }

  return (
    <div className="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm">
      {groups.map((group) => (
        <div key={group.key}>
          <div className="sticky top-0 z-10 flex items-baseline justify-between border-b border-gray-100 bg-gray-50/95 px-4 py-1.5 backdrop-blur">
            <h3 className="text-xs font-semibold uppercase tracking-wide text-gray-600">{group.label}</h3>
            <p className="text-xs tabular-nums text-gray-500">
              {group.items.length} facture{group.items.length > 1 ? 's' : ''} ·{' '}
              {Object.entries(group.totals).map(([currency, amount]) => money(amount, currency)).join(' + ')} HT
            </p>
          </div>
          <ul className="divide-y divide-gray-100">
            {group.items.map((item) => (
              <li key={item.id}>
                <button
                  type="button"
                  onClick={() => onOpen(item)}
                  className={`grid w-full grid-cols-[3.25rem_1fr_auto] items-center gap-x-3 px-4 py-2 text-left transition hover:bg-blue-50/50 md:grid-cols-[3.5rem_minmax(0,1.6fr)_minmax(0,1.2fr)_6.5rem_3.5rem]
                    ${selectedId === item.id ? 'bg-blue-50' : ''}`}
                >
                  <span className="text-xs tabular-nums text-gray-500">{dayLabel(item.date)}</span>
                  <span className="min-w-0">
                    <span className="block truncate text-sm font-medium text-gray-900">{item.vendor || 'Fournisseur inconnu'}</span>
                    <span className="block truncate text-xs text-gray-500">
                      {item.lines_count
                        ? item.lines_preview.map((l) => `${qty(l.quantity)} × ${l.designation}`).join(' · ')
                        : item.description || '—'}
                    </span>
                  </span>
                  <span className="hidden min-w-0 md:block"><ProjectsCell projects={item.projects} /></span>
                  <span className="text-right">
                    <span className="block text-sm font-semibold tabular-nums text-gray-900">{money(item.amount, item.currency)}</span>
                    {item.tva != null && <span className="block text-[11px] tabular-nums text-gray-400">{money((Number(item.amount) || 0) + (Number(item.tva) || 0), item.currency)} TTC</span>}
                  </span>
                  <span className="hidden items-center justify-end gap-1.5 md:flex">
                    {item.lines_count > 0 && <Layers className="h-3.5 w-3.5 text-violet-400" title={`${item.lines_count} lignes relevées`} />}
                    <PennylaneIcon status={item.pennylane_status} />
                    {item.file_url && (
                      <a href={fileHref(item.file_url)} target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()}
                        className="text-gray-400 hover:text-blue-600" title="Ouvrir le justificatif">
                        <Paperclip className="h-3.5 w-3.5" />
                      </a>
                    )}
                  </span>
                  <span className="col-span-3 mt-1 md:hidden"><ProjectsCell projects={item.projects} /></span>
                </button>
              </li>
            ))}
          </ul>
        </div>
      ))}
      <div className="flex items-center justify-center border-t border-gray-100 px-4 py-2 text-xs text-gray-500">
        {loading ? <Spinner /> : items.length < total ? (
          <button type="button" onClick={onMore} className="font-medium text-blue-700 hover:underline">
            Afficher plus ({total - items.length} restantes)
          </button>
        ) : <span>{total} facture{total > 1 ? 's' : ''}</span>}
      </div>
    </div>
  );
};

export default InvoiceList;
